import { ReactElement } from 'react';
import { useTheme, TableContainer, TableHead, TableCell, TableBody } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CriteriaResult, CombinationCalculationType } from 'types/domainTypes';
import { ResultsPanelAccordion } from 'components/ResultsPanelAccordion/ResultsPanelAccordion';
import { TableSectionHeaderRow } from 'components/TableSectionHeaderRow/TableSectionHeaderRow';
import { CriteriaResultsRow } from 'components/CriteriaResultsRow/CriteriaResultsRow';
import { ResultsPanelTable } from './CalculationResultsPanel';
import { TableRow } from 'components/atoms/TableRow';

interface WorstPerformingCombinationsViewProps {
  calculationId: string;
  criteria: CriteriaResult[];
  expanded: boolean;
  onClick: () => void;
}

export function WorstPerformingCombinationsView({
  calculationId,
  criteria,
  expanded,
  onClick,
}: WorstPerformingCombinationsViewProps): ReactElement {
  const { t } = useTranslation();
  const theme = useTheme();

  function getResultForCalculationType(calculationType: CombinationCalculationType): CriteriaResult | undefined {
    return criteria.find((result) => result.calculationType === calculationType);
  }

  const deflection = getResultForCalculationType(CombinationCalculationType.Deflection);
  const shear = getResultForCalculationType(CombinationCalculationType.Shear);
  const wrinklingYieldingExternal = getResultForCalculationType(CombinationCalculationType.WrinklingYieldingExternal);
  const wrinklingYieldingInternal = getResultForCalculationType(CombinationCalculationType.WrinklingYieldingInternal);
  const shearUls = getResultForCalculationType(CombinationCalculationType.ShearUls);
  const wrinklingYieldingExternalUls = getResultForCalculationType(CombinationCalculationType.WrinklingYieldingExternalUls);
  const wrinklingYieldingInternalUls = getResultForCalculationType(CombinationCalculationType.WrinklingYieldingInternalUls);

  return (
    <ResultsPanelAccordion
      header={t('calculation-page.results-panel.worst-performing-combinations.header')}
      greyOutHeader={t('calculation-page.results-panel.worst-performing-combinations.header-per-criteria')}
      expanded={expanded}
      onClick={onClick}
      data-testid="worst-performing-combinations-accordion"
    >
      <TableContainer>
        <ResultsPanelTable>
          <TableHead>
            <TableRow sx={{ '& th': { color: theme.palette.grey[500] } }}>
              <TableCell align="left">{t('calculation-page.results-panel.worst-performing-combinations.label-criteria')}</TableCell>
              <TableCell align="left">{t('calculation-page.results-panel.worst-performing-combinations.label-result')}</TableCell>
              <TableCell align="center">{t('calculation-page.results-panel.worst-performing-combinations.label-status')}</TableCell>
              <TableCell align="center">{t('calculation-page.results-panel.worst-performing-combinations.label-graph')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableSectionHeaderRow
              label={t('calculation-page.results-panel.worst-performing-combinations.label-serviceable-limit-state')}
            />
            {deflection && (
              <CriteriaResultsRow
                calculationId={calculationId}
                criteriaResult={deflection}
                label={t('calculation-page.results-panel.worst-performing-combinations.label-deflection')}
                data-testid={'deflection-sls'}
              />
            )}
            {shear && (
              <CriteriaResultsRow
                calculationId={calculationId}
                criteriaResult={shear}
                label={t('calculation-page.results-panel.worst-performing-combinations.label-shear')}
                data-testid={'shear-sls'}
              />
            )}
            {wrinklingYieldingExternal && (
              <CriteriaResultsRow
                calculationId={calculationId}
                criteriaResult={wrinklingYieldingExternal}
                label={t('calculation-page.results-panel.worst-performing-combinations.label-wrinkling-yielding-external')}
                data-testid={'wrinkling-yielding-external-sls'}
              />
            )}
            {wrinklingYieldingInternal && (
              <CriteriaResultsRow
                calculationId={calculationId}
                criteriaResult={wrinklingYieldingInternal}
                label={t('calculation-page.results-panel.worst-performing-combinations.label-wrinkling-yielding-internal')}
                data-testid={'wrinkling-yielding-internal-sls'}
              />
            )}
            <TableSectionHeaderRow label={t('calculation-page.results-panel.worst-performing-combinations.label-ultimate-limit-state')} />
            {shearUls && (
              <CriteriaResultsRow
                calculationId={calculationId}
                criteriaResult={shearUls}
                label={t('calculation-page.results-panel.worst-performing-combinations.label-shear')}
                data-testid={'shear-uls'}
              />
            )}
            {wrinklingYieldingExternalUls && (
              <CriteriaResultsRow
                calculationId={calculationId}
                criteriaResult={wrinklingYieldingExternalUls}
                label={t('calculation-page.results-panel.worst-performing-combinations.label-wrinkling-yielding-external')}
                data-testid={'wrinkling-yielding-external-uls'}
              />
            )}
            {wrinklingYieldingInternalUls && (
              <CriteriaResultsRow
                calculationId={calculationId}
                criteriaResult={wrinklingYieldingInternalUls}
                label={t('calculation-page.results-panel.worst-performing-combinations.label-wrinkling-yielding-internal')}
                data-testid={'wrinkling-yielding-internal-uls'}
              />
            )}
          </TableBody>
        </ResultsPanelTable>
      </TableContainer>
    </ResultsPanelAccordion>
  );
}
